import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
} from 'react';

import { getEventSource, closeEventSource } from '../../queries/sseClient';
import {BACKEND_URL, SSE_OUTPUT_CHANNEL} from "../../constants/globalConstants";

const backendBaseURL = BACKEND_URL;
const url = backendBaseURL + `/output_sse/outputSSE`;

// Struttura dati normalizzata che girerà dentro la nostra app React
export interface SSEEvent {
    entity: string;    // es. "collection"
    operation: string; // es. "update", "create", "delete"
    payload: any;      // Il contenuto effettivo
}

type SSEListener = (event: SSEEvent) => void;

interface OutputSSEContextType {
    openOutputSSE: () => (() => void) | void;
    subscribe: (listener: SSEListener) => () => void;
    closeOutputSSE: () => void;
}

const OutputSSEProviderContext = createContext<OutputSSEContextType | undefined>(undefined);

interface OutputSSEProviderProps {
    children: React.ReactNode;
}

const OutputSSEProvider = ({ children }: OutputSSEProviderProps) => {
    const eventSourceRef = useRef<any>(null);
    const listenersRef = useRef<Set<SSEListener>>(new Set());

    const subscribe = (listener: SSEListener) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    };

    function openOutputSSE() {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            closeEventSource(SSE_OUTPUT_CHANNEL);
            eventSourceRef.current = null;
            return;
        }

        const es: any = getEventSource(url, SSE_OUTPUT_CHANNEL);
        if (!es) return;

        eventSourceRef.current = es.eventSource;

        const handleUpdate = async (event: MessageEvent) => {
            try {
                const rawData = JSON.parse(event.data);

                if (rawData.type && rawData.type.startsWith('/')) {
                    const parts = rawData.type.split('/');

                    if (parts.length >= 3) {
                        const operation = parts[1];
                        const entity = parts[2];

                        const normalizedEvent: SSEEvent = {
                            entity,
                            operation,
                            payload: rawData.payload
                        };
                        listenersRef.current.forEach((listener) => listener(normalizedEvent));
                    }
                }
            } catch (error) {
                console.error("Output SSE - SSE error during message parsing:", error);
            }
        };

        const handleError = (err: Event) => {
            console.error('Output SSE Error:', err);
        };

        if (es.eventSource !== null && !es.isConnect) {
            es.eventSource.addEventListener('message', handleUpdate);
            es.eventSource.addEventListener('error', handleError);
        }

        return () => {
            if (es.eventSource !== null) {
                es.eventSource.removeEventListener('message', handleUpdate);
                es.eventSource.removeEventListener('error', handleError);
            }
            closeEventSource(SSE_OUTPUT_CHANNEL);
        };
    }

    useEffect(() => {
        const handleUnload = () => {
            closeEventSource(SSE_OUTPUT_CHANNEL);
        };

        window.addEventListener('beforeunload', handleUnload);
        return () => {
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, []);

    return (
        <OutputSSEProviderContext.Provider value={{ openOutputSSE, subscribe, closeOutputSSE: () => closeEventSource(SSE_OUTPUT_CHANNEL) }}>
            {children}
        </OutputSSEProviderContext.Provider>
    );
};

const useOutputSSEProvider = (): OutputSSEContextType => {
    const context = useContext(OutputSSEProviderContext);
    if (context === undefined) {
        throw new Error('SSEProvider must be used within a SSE provider');
    }
    return context;
};

export { OutputSSEProvider, useOutputSSEProvider };